import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Trash2, 
  Edit2, 
  Copy, 
  ExternalLink, 
  Clock,
  Bookmark
} from 'lucide-react';
import { useQR } from '@/hooks/useQRContext';
import { useToast } from '@/components/common/Toast';
import { Button, Card, Modal } from '@/components/common';
import { formatDate, truncate, copyToClipboard } from '@/utils/helpers';

/**
 * Saved designs page - view, edit, and manage saved QR codes
 */
function SavedPage() {
  const navigate = useNavigate();
  const { savedDesigns, loadDesign, deleteDesign, getShareableLink, setQRConfig } = useQR();
  const { success, error } = useToast();

  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [designToDelete, setDesignToDelete] = useState(null);

  // Handle load design
  const handleLoadDesign = (design) => {
    loadDesign(design.id);
    navigate('/generator');
  };

  // Handle duplicate design
  const handleDuplicate = (design) => {
    setQRConfig({ ...design.config, data: design.config.data });
    navigate('/generator');
    success('Design duplicated! Make your changes and save as a new design.');
  };

  // Handle share design
  const handleShare = async (design) => {
    setQRConfig(design.config);
    const link = getShareableLink();
    const copied = await copyToClipboard(link);
    
    if (copied) {
      success('Shareable link copied to clipboard!');
    } else {
      error('Failed to copy link');
    }
  };

  // Confirm delete
  const handleConfirmDelete = () => {
    if (designToDelete) {
      deleteDesign(designToDelete.id);
      success(`"${designToDelete.name}" deleted`);
      setDesignToDelete(null);
      setIsDeleteModalOpen(false);
    }
  };

  // Open delete modal
  const openDeleteModal = (design) => {
    setDesignToDelete(design);
    setIsDeleteModalOpen(true);
  };

  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Page Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Saved Designs</h1>
            <p className="text-gray-600 mt-1">
              {savedDesigns.length} saved design{savedDesigns.length !== 1 ? 's' : ''}
            </p>
          </div>
          <Button
            variant="primary"
            onClick={() => navigate('/generator')}
          >
            Create New
          </Button>
        </div>

        {/* Empty State */}
        {savedDesigns.length === 0 ? (
          <Card className="text-center py-16">
            <Bookmark className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h2 className="text-xl font-semibold text-gray-900 mb-2">
              No saved designs yet
            </h2>
            <p className="text-gray-600 mb-6 max-w-md mx-auto">
              Create a QR code and save it to access it later. Your designs are 
              stored locally in your browser.
            </p>
            <Button
              variant="primary"
              onClick={() => navigate('/generator')}
            >
              Create Your First QR Code
            </Button>
          </Card>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {savedDesigns.map((design) => (
              <Card key={design.id} className="group">
                {/* Preview */}
                <div 
                  className="aspect-square rounded-xl mb-4 flex items-center justify-center cursor-pointer relative overflow-hidden"
                  style={{ backgroundColor: design.config.backgroundColor }}
                  onClick={() => handleLoadDesign(design)}
                >
                  <div 
                    className="w-3/4 h-3/4 flex items-center justify-center"
                    style={{ color: design.config.dotsColor }}
                  >
                    {/* Simple QR preview placeholder */}
                    <svg viewBox="0 0 100 100" className="w-full h-full">
                      <rect x="10" y="10" width="25" height="25" rx="3" fill="currentColor" />
                      <rect x="65" y="10" width="25" height="25" rx="3" fill="currentColor" />
                      <rect x="10" y="65" width="25" height="25" rx="3" fill="currentColor" />
                      <rect x="15" y="15" width="15" height="15" rx="2" fill={design.config.backgroundColor} />
                      <rect x="70" y="15" width="15" height="15" rx="2" fill={design.config.backgroundColor} />
                      <rect x="15" y="70" width="15" height="15" rx="2" fill={design.config.backgroundColor} />
                      <rect x="18" y="18" width="9" height="9" rx="1" fill="currentColor" />
                      <rect x="73" y="18" width="9" height="9" rx="1" fill="currentColor" />
                      <rect x="18" y="73" width="9" height="9" rx="1" fill="currentColor" />
                      {/* Pattern */}
                      <rect x="40" y="10" width="8" height="8" rx="1" fill="currentColor" />
                      <rect x="52" y="10" width="8" height="8" rx="1" fill="currentColor" />
                      <rect x="40" y="22" width="8" height="8" rx="1" fill="currentColor" />
                      <rect x="10" y="40" width="8" height="8" rx="1" fill="currentColor" />
                      <rect x="22" y="40" width="8" height="8" rx="1" fill="currentColor" />
                      <rect x="40" y="40" width="20" height="20" rx="2" fill="currentColor" />
                      <rect x="65" y="40" width="8" height="8" rx="1" fill="currentColor" />
                      <rect x="80" y="40" width="10" height="8" rx="1" fill="currentColor" />
                      <rect x="65" y="65" width="25" height="25" rx="2" fill="currentColor" />
                      <rect x="70" y="70" width="15" height="15" rx="1" fill={design.config.backgroundColor} />
                    </svg>
                  </div>

                  {/* Hover overlay */}
                  <div className="absolute inset-0 bg-black/50 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                    <Button variant="secondary" size="sm">
                      <Edit2 className="w-4 h-4" />
                      Edit
                    </Button>
                  </div>
                </div>

                {/* Info */}
                <div>
                  <h3 className="font-semibold text-gray-900 truncate">
                    {design.name}
                  </h3>
                  <p className="text-sm text-gray-500 truncate mt-1">
                    {truncate(design.config.data, 40) || 'No content'}
                  </p>
                  <div className="flex items-center gap-1 text-xs text-gray-400 mt-2">
                    <Clock className="w-3 h-3" />
                    {formatDate(design.createdAt)}
                  </div>
                </div>

                {/* Actions */}
                <div className="flex items-center gap-2 mt-4 pt-4 border-t border-gray-100">
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleDuplicate(design)}
                    title="Duplicate"
                  >
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => handleShare(design)}
                    title="Share"
                  >
                    <ExternalLink className="w-4 h-4" />
                  </Button>
                  <div className="flex-1" />
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => openDeleteModal(design)}
                    className="text-red-500 hover:text-red-600 hover:bg-red-50"
                    title="Delete"
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              </Card>
            ))}
          </div>
        )}

        {/* Storage Info */}
        {savedDesigns.length > 0 && (
          <div className="mt-8 p-4 bg-blue-50 rounded-xl text-sm text-blue-700">
            <strong>Note:</strong> Designs are saved in your browser&apos;s local storage. 
            They will not sync across devices or browsers. For permanent backup, use the 
            share link to save your designs.
          </div>
        )}
      </div>

      {/* Delete Confirmation Modal */}
      <Modal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        title="Delete Design"
        description={`Are you sure you want to delete "${designToDelete?.name}"? This action cannot be undone.`}
      >
        <div className="flex gap-3 justify-end mt-4">
          <Button
            variant="secondary"
            onClick={() => setIsDeleteModalOpen(false)}
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={handleConfirmDelete}
          >
            Delete
          </Button>
        </div>
      </Modal>
    </div>
  );
}

export default SavedPage;
